import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  TextField
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteBetLimit, updateBetLimit } from 'services/games';

const darkFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.06)',
    '& input, & textarea': {
      color: '#d3ddf5',
      WebkitTextFillColor: '#d3ddf5'
    },
    '& fieldset': { borderColor: 'rgba(34,211,238,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(34,211,238,0.45)' },
    '&.Mui-focused fieldset': { borderColor: '#22d3ee' }
  },
  '& .MuiInputLabel-root': { color: 'rgba(223,232,250,0.88)' },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': { color: 'rgba(223,232,250,0.82)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#22d3ee' },
  '& .MuiInputBase-input': {
    color: '#e6eefc',
    WebkitTextFillColor: '#e6eefc'
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(223,232,250,0.62)',
    opacity: 1
  },
  '& .MuiFormHelperText-root, & .MuiFormHelperText-root.MuiFormHelperText-root': {
    color: '#9fd3ff',
    marginLeft: 0,
    marginRight: 0
  }
};

export default function BetLimit({
  id,
  minBet,
  maxBet,
  gameId,
  singleBet,
  initialEnabled,
  initialRequireAccessCode
}) {
  const [editBetLimitMin, setEditBetLimitMin] = useState(minBet);
  const [editBetLimitMax, setEditBetLimitMax] = useState(maxBet);
  const [editBetLimitSingle, setEditBetLimitSingle] = useState(singleBet);
  const [enabled, setEnabled] = useState(initialEnabled !== false);
  const [requireAccessCode, setRequireAccessCode] = useState(!!initialRequireAccessCode);

  const [openDeleteBetLimit, setOpenDeleteBetLimit] = useState();
  const handleOpenDeleteBetLimit = () => setOpenDeleteBetLimit(true);
  const handleCloseDeleteBetLimit = () => setOpenDeleteBetLimit(false);

  const [errorMessage, setErrorMessage] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [updated, setUpdated] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();

  const getRequestErrorMessage = (err, fallbackMessage) => {
    const message = err?.response?.data?.message;
    if (Array.isArray(message)) {
      return message.join(', ');
    }
    if (typeof message === 'string' && message.trim() !== '') {
      return message;
    }
    if (typeof err?.message === 'string' && err.message.trim() !== '') {
      return err.message;
    }
    return fallbackMessage;
  };

  useEffect(() => {
    setTimeout(() => setUpdated(false), 5000);
  }, [updated]);

  const updateBetLimitBtn = async () => {
    const dto = {
      gameId: Number(gameId),
      singleBet: editBetLimitSingle ? Number(editBetLimitSingle) : editBetLimitSingle,
      requireAccessCode
    };
    if (minBet) dto.minBet = editBetLimitMin ? Number(editBetLimitMin) : editBetLimitMin;
    if (maxBet) dto.maxBet = editBetLimitMax ? Number(editBetLimitMax) : editBetLimitMax;
    setRequestError('');
    try {
      await updateBetLimit(id, dto);
      setUpdated(true);
    } catch (err) {
      setUpdated(false);
      setRequestError(getRequestErrorMessage(err, 'Failed to update bet limit.'));
    }
  };

  const toggleEnabledBtn = async () => {
    const nextEnabled = !enabled;
    setToggleLoading(true);
    setRequestError('');
    try {
      await updateBetLimit(id, { enabled: nextEnabled });
      setEnabled(nextEnabled);
      setUpdated(true);
    } catch (err) {
      setUpdated(false);
      setRequestError(getRequestErrorMessage(err, 'Failed to update bet limit status.'));
    } finally {
      setToggleLoading(false);
    }
  };

  const handleConfirmDeleteBetLimit = async () => {
    setDeleteLoading(true);
    setRequestError('');
    try {
      await deleteBetLimit(id);
      handleCloseDeleteBetLimit();
      navigate(0);
    } catch (err) {
      setRequestError(getRequestErrorMessage(err, 'Failed to delete bet limit.'));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <MDBox
        sx={{
          border: '1px solid rgba(90,163,255,0.2)',
          borderRadius: '8px',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          width: { xs: '100%', sm: 'calc(50% - 10px)', lg: '23%' },
          background: 'linear-gradient(160deg, #111d38 0%, #162848 55%, #0e1a2e 100%)'
        }}
      >
        {minBet != null && Number(minBet) > 0 && (
          <TextField
            label='Min Bet'
            value={editBetLimitMin}
            onChange={(e) => setEditBetLimitMin(e.target.value)}
            type='number'
            fullWidth
            variant='outlined'
            sx={darkFieldSx}
          />
        )}
        {maxBet != null && Number(maxBet) > 0 && (
          <TextField
            label='Max Bet'
            value={editBetLimitMax}
            onChange={(e) => setEditBetLimitMax(e.target.value)}
            type='number'
            fullWidth
            variant='outlined'
            sx={darkFieldSx}
          />
        )}
        {singleBet && (
          <TextField
            label='Single Bet'
            value={editBetLimitSingle}
            onChange={(e) => setEditBetLimitSingle(e.target.value)}
            type='number'
            fullWidth
            variant='outlined'
            sx={darkFieldSx}
          />
        )}
        <FormControlLabel
          sx={{ '& .MuiFormControlLabel-label': { color: '#d3ddf5' } }}
          control={
            <Checkbox
              checked={requireAccessCode}
              onChange={(e) => setRequireAccessCode(e.target.checked)}
              sx={{ color: 'rgba(211,221,245,0.5)', '&.Mui-checked': { color: '#22d3ee' } }}
            />
          }
          label='Require access code'
        />
        <MDBox
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            borderRadius: '8px',
            border: `1px solid ${enabled ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.45)'}`,
            background: enabled ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)'
          }}
        >
          <MDTypography
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: enabled ? '#22c55e' : '#ef4444'
            }}
          >
            {enabled ? 'Active' : 'Inactive'}
          </MDTypography>
          <MDButton
            color={enabled ? 'error' : 'success'}
            size='small'
            disabled={toggleLoading}
            onClick={toggleEnabledBtn}
          >
            {toggleLoading ? 'Working...' : enabled ? 'Disable' : 'Enable'}
          </MDButton>
        </MDBox>
        {errorMessage && (
          <MDBox sx={{ fontSize: '12px', textAlign: 'center', color: '#e71616' }}>
            Max Bet must be greater than Min Bet.
          </MDBox>
        )}
        {requestError && (
          <MDBox sx={{ fontSize: '12px', textAlign: 'center', color: '#ff8a80' }}>
            {requestError}
          </MDBox>
        )}
        {updated && (
          <MDBox sx={{ fontSize: '12px', textAlign: 'center', color: '#fff' }}>Bet limit updated successfully.</MDBox>
        )}
        <MDBox sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '10px', gap: '8px' }}>
          <MDButton
            color='info'
            sx={{ width: '50%' }}
            onClick={() => {
              if (Number(editBetLimitMin) >= Number(editBetLimitMax) && singleBet === null) {
                setErrorMessage(true);
              } else {
                setErrorMessage(false);
                updateBetLimitBtn();
              }
            }}
          >
            Update
          </MDButton>
          <MDButton color='error' sx={{ width: '50%' }} onClick={handleOpenDeleteBetLimit}>
            Delete
          </MDButton>
        </MDBox>
      </MDBox>

      <Dialog open={openDeleteBetLimit} onClose={handleCloseDeleteBetLimit} aria-labelledby='alert-dialog-title'>
        <DialogTitle id='alert-dialog-title' sx={{ textAlign: 'center' }}>
          Are you sure you want to delete this bet limit?
        </DialogTitle>
        <DialogActions>
          <MDButton
            variant='text'
            color='error'
            disabled={deleteLoading}
            onClick={handleConfirmDeleteBetLimit}
          >
            {deleteLoading ? 'Deleting...' : 'Yes'}
          </MDButton>
          <MDButton variant='text' disabled={deleteLoading} onClick={handleCloseDeleteBetLimit}>
            No
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
